import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { todayIn } from "../../../shared/dates";
import { formatTakenDate, seededShuffle, type Photo } from "../../../shared/photos";
import { setDevicePref, SCREENSAVER_DEFAULTS, useDevicePrefs, type ScreensaverPrefs } from "../../app/devicePrefs";
import { useHousehold } from "../../app/useHousehold";
import { isTyping, TYPING_HOLD_MS } from "../../ui/keepTyping";
import { formatTime, useNow } from "../../ui/time";
import { usePhotoFeedInfo, usePhotoLibrary } from "./api";
import { PhotoImage, useBoxSize, usePageVisible, useStillPhotos, variantFor } from "./PhotoView";
import "./photos.css";

/** Start the screensaver now (the settings' Preview button). */
export const SCREENSAVER_PREVIEW = "kinmantle:screensaver-preview";
const ACTIVITY = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"] as const;
const CHECK_MS = 5_000;

/**
 * Photo screensaver: after this screen has been left alone for a while, family photos
 * fill it (with an optional clock) until someone touches it. Configured per screen in
 * Settings → Display. Never while editing, during an alert takeover, or (if asked) at night.
 */
export function Screensaver({ hold, night }: { hold: boolean; night: boolean }) {
  const prefs = useDevicePrefs().screensaver;
  const [active, setActive] = useState(false);
  const last = useRef(Date.now());
  const blocked = hold || (prefs.skipNight && night);

  useEffect(() => {
    const touch = () => {
      last.current = Date.now();
    };
    for (const e of ACTIVITY) window.addEventListener(e, touch, { capture: true, passive: true });
    const preview = () => {
      last.current = 0;
      setActive(true);
    };
    window.addEventListener(SCREENSAVER_PREVIEW, preview);
    return () => {
      for (const e of ACTIVITY) window.removeEventListener(e, touch, { capture: true });
      window.removeEventListener(SCREENSAVER_PREVIEW, preview);
    };
  }, []);

  useEffect(() => {
    if (!prefs.enabled || blocked) return;
    const t = window.setInterval(() => {
      const idle = Date.now() - last.current;
      // Someone mid-typing gets longer before the photos cover their text box.
      if (idle >= prefs.idleMinutes * 60_000 && !(isTyping() && idle < TYPING_HOLD_MS)) setActive(true);
    }, CHECK_MS);
    return () => clearInterval(t);
  }, [prefs.enabled, prefs.idleMinutes, blocked]);

  useEffect(() => {
    if (blocked) setActive(false);
  }, [blocked]);

  const dismiss = useCallback(() => {
    last.current = Date.now();
    setActive(false);
  }, []);

  if (!active) return null;
  return createPortal(<ScreensaverShow prefs={prefs} onDismiss={dismiss} />, document.body);
}

interface Layer {
  key: number;
  photo: Photo;
  loaded: boolean;
}

function ScreensaverShow({ prefs, onDismiss }: { prefs: ScreensaverPrefs; onDismiss: () => void }) {
  const { timeZone, settings } = useHousehold();
  const now = useNow("minute");
  const date = todayIn(timeZone, now);
  const memories = prefs.photos === "memories";
  const q = usePhotoLibrary(memories ? "" : prefs.photos, memories ? date : undefined);
  const still = useStillPhotos();
  const visible = usePageVisible();
  const [boxRef, box] = useBoxSize<HTMLDivElement>();
  const [seed] = useState(() => Math.random().toString(36).slice(2));
  const order = useMemo(() => seededShuffle(q.data ?? [], seed), [q.data, seed]);
  const [layers, setLayers] = useState<Layer[]>([]);
  const keyRef = useRef(0);
  const index = useRef(-1);
  const transition = still && prefs.transition !== "none" ? "fade" : prefs.transition;

  const advance = useCallback(() => {
    if (!order.length) return;
    index.current = (index.current + 1) % order.length;
    const photo = order[index.current]!;
    keyRef.current += 1;
    setLayers((ls) => [...ls.slice(-1), { key: keyRef.current, photo, loaded: false }]);
  }, [order]);

  useEffect(() => {
    index.current = -1;
    advance();
  }, [advance]);

  const current = layers[layers.length - 1];
  useEffect(() => {
    if (!current?.loaded || !visible || order.length < 2) return;
    const t = window.setTimeout(advance, prefs.secondsPerPhoto * 1000);
    return () => clearTimeout(t);
  }, [current?.key, current?.loaded, visible, order.length, prefs.secondsPerPhoto, advance]);

  const fadeMs = transition === "none" ? 0 : transition === "slide" ? 1100 : 1600;
  useEffect(() => {
    if (layers.length < 2 || !current?.loaded) return;
    const t = window.setTimeout(() => setLayers((ls) => ls.slice(-1)), fadeMs + 150);
    return () => clearTimeout(t);
  }, [layers.length, current?.loaded, current?.key, fadeMs]);

  const next = order.length > 1 ? order[(index.current + 1) % order.length] : undefined;
  const nextSrc = next && box.w ? (variantFor(next, box, transition === "zoom") === "full" ? next.url : next.thumbUrl) : undefined;
  useEffect(() => {
    if (!nextSrc) return;
    const img = new Image();
    img.decoding = "async";
    img.src = nextSrc;
    return () => {
      img.src = "";
    };
  }, [nextSrc]);

  // Nothing to show (no photos yet, or the source is down): don't blank the screen.
  useEffect(() => {
    if (q.isFetched && !order.length) onDismiss();
  }, [q.isFetched, order.length, onDismiss]);

  // The first touch only wakes the screen; it doesn't reach what's underneath (closing on
  // click, not pointerdown, keeps the whole tap on this overlay).
  const swallow = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDismiss();
  };
  useEffect(() => {
    const key = () => onDismiss();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onDismiss]);

  const shown = current?.photo;
  const taken = formatTakenDate(shown?.takenAt ?? null);
  return (
    <div
      ref={boxRef}
      className={`ss ss-${transition} ${still ? "is-still" : ""}`}
      role="dialog"
      aria-label="Photo screensaver. Touch to return."
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={swallow}
    >
      {layers.map((l) => {
        const isTop = l.key === current?.key;
        return (
          <div key={l.key} className={`pf-layer ss-layer ${isTop ? (l.loaded ? "on" : "") : "out"}`} style={{ ["--fade" as string]: `${fadeMs}ms` }}>
            <PhotoImage
              photo={l.photo}
              box={box}
              still={still}
              motion={transition === "zoom"}
              durationMs={prefs.secondsPerPhoto * 1000 + fadeMs * 2}
              onLoad={() => setLayers((ls) => ls.map((x) => (x.key === l.key ? { ...x, loaded: true } : x)))}
              onError={isTop ? () => window.setTimeout(advance, 800) : undefined}
            />
          </div>
        );
      })}
      {(shown?.caption || taken) && prefs.clock !== "large" && (
        <div className="ss-caption">
          {shown?.caption && <strong>{shown.caption}</strong>}
          {taken && <span>{taken}</span>}
        </div>
      )}
      {prefs.clock !== "off" && (
        <div className={`ss-clock ss-clock-${prefs.clock}`} aria-hidden="true">
          <strong>{formatTime(now, timeZone, settings.clock24h)}</strong>
          <span>{now.toLocaleDateString(undefined, { timeZone, weekday: "long", month: "long", day: "numeric" })}</span>
        </div>
      )}
    </div>
  );
}

/** Settings → Display: the screensaver for this screen. */
export function ScreensaverSettings() {
  const prefs = useDevicePrefs().screensaver;
  const info = usePhotoFeedInfo();
  const set = (patch: Partial<ScreensaverPrefs>) => setDevicePref("screensaver", { ...SCREENSAVER_DEFAULTS, ...prefs, ...patch });
  const chips = <K extends keyof ScreensaverPrefs>(key: K, options: readonly (readonly [ScreensaverPrefs[K], string])[]) => (
    <div className="chip-row wrap">
      {options.map(([value, label]) => (
        <button key={String(value)} className={`chip ${prefs[key] === value ? "on" : ""}`} onClick={() => set({ [key]: value } as Partial<ScreensaverPrefs>)}>
          {label}
        </button>
      ))}
    </div>
  );
  const albums = info.data?.albums ?? [];
  const tags = (info.data?.tags ?? []).slice(0, 16);
  return (
    <>
      <label className="field field-toggle">
        <span>
          Photo screensaver on this screen
          <small className="muted">Shows family photos when nobody has touched this screen for a while. A touch brings the screen back.</small>
        </span>
        <input type="checkbox" className="switch" checked={prefs.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
      </label>
      {prefs.enabled && (
        <div className="ss-settings">
          <div className="field">
            <span>Starts after</span>
            {chips("idleMinutes", [
              [2, "2 min"],
              [5, "5 min"],
              [10, "10 min"],
              [20, "20 min"],
              [30, "30 min"],
              [60, "1 hour"],
            ])}
          </div>
          <div className="field">
            <span>Photos</span>
            <div className="chip-row wrap">
              <button className={`chip ${prefs.photos === "" ? "on" : ""}`} onClick={() => set({ photos: "" })}>
                All wall photos
              </button>
              <button className={`chip ${prefs.photos === "memories" ? "on" : ""}`} onClick={() => set({ photos: "memories" })}>
                On this day
              </button>
              {[...albums, ...tags].map((a, i) => (
                <button key={`${i < albums.length ? "folder" : "tag"}:${a.name}`} className={`chip ${prefs.photos === a.name ? "on" : ""}`} onClick={() => set({ photos: a.name })}>
                  {a.name} <span className="muted">{a.count.toLocaleString()}</span>
                </button>
              ))}
            </div>
            <p className="muted small">Only photos already approved for the wall. “On this day” shows photos taken on today’s date in earlier years.</p>
          </div>
          <div className="field">
            <span>Change photo every</span>
            {chips("secondsPerPhoto", [
              [10, "10 s"],
              [20, "20 s"],
              [30, "30 s"],
              [60, "1 min"],
            ])}
          </div>
          <div className="field">
            <span>Transition</span>
            {chips("transition", [
              ["zoom", "Slow zoom"],
              ["fade", "Fade"],
              ["slide", "Slide"],
              ["none", "None"],
            ])}
          </div>
          <div className="field">
            <span>Clock</span>
            {chips("clock", [
              ["corner", "In the corner"],
              ["large", "Large"],
              ["off", "Off"],
            ])}
          </div>
          <label className="field field-toggle">
            <span>
              Not during night hours
              <small className="muted">On screens that follow the day schedule, the screen stays dim at night instead.</small>
            </span>
            <input type="checkbox" className="switch" checked={prefs.skipNight} onChange={(e) => set({ skipNight: e.target.checked })} />
          </label>
          <div>
            <button className="btn" onClick={() => window.dispatchEvent(new Event(SCREENSAVER_PREVIEW))}>
              Preview
            </button>
          </div>
        </div>
      )}
    </>
  );
}
